import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { phappsRequest } from "@/services/apiService";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  TextField,
  InputAdornment,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import { API_PATHS } from "@/config/apiConstants";
import AdminQudaStatCard from "@/components/admin/AdminQudaStatCard";
import ChartCard from "@/components/data-visuals/ChartCard";
import { QUDA_COLORS } from "@/config/colors";
import { ShoppingCart, TrendingUp, Search } from "@mui/icons-material";
import { getChemistName } from "@/utils/textUtils";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import {
  ADMIN_TOP_CARD_HEIGHT,
  adminDateRangePickerWideNestedSx,
  qudaAdminDataGridSx,
} from "@/utils/adminTableStyles";

const preprocessOrders = (orders, dateStart, dateEnd) => {
  if (!orders || !Array.isArray(orders)) return [];
  const start = new Date(dateStart);
  const end = new Date(dateEnd);
  const dateMap = new Map();

  orders.forEach((o) => {
    const key = new Date(o.OrderDate).toISOString().split("T")[0];
    if (!dateMap.has(key)) {
      dateMap.set(key, {
        OrderDate: key,
        TotalOrders: 0,
        DispensaryOrders: 0,
        OTCOrders: 0,
      });
    }
    const existing = dateMap.get(key);
    existing.TotalOrders += parseInt(o.TotalOrders || 0, 10);
    existing.DispensaryOrders += parseInt(o.DispensaryOrders || 0, 10);
    existing.OTCOrders += parseInt(o.OTCOrders || 0, 10);
    dateMap.set(key, existing);
  });

  const result = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().split("T")[0];
    result.push(
      dateMap.get(key) || {
        OrderDate: key,
        TotalOrders: 0,
        DispensaryOrders: 0,
        OTCOrders: 0,
      }
    );
  }
  return result;
};

const qudaDateRangeSx = {
  borderRadius: "8px",
  p: 2,
  height: ADMIN_TOP_CARD_HEIGHT,
  minHeight: ADMIN_TOP_CARD_HEIGHT,
  maxHeight: ADMIN_TOP_CARD_HEIGHT,
  boxSizing: "border-box",
  width: "100%",
  minWidth: 0,
  backgroundColor: QUDA_COLORS.TEAL_DARK,
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  "& .text-black": { color: "white !important" },
  ...adminDateRangePickerWideNestedSx,
};

const AdminQudaPage = ({ isManager = false }) => {
  const navigate = useNavigate();
  const chemistBasePath = isManager ? "/manager-functions/chemist" : "/admin-functions/chemist";
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [chemistFilter, setChemistFilter] = useState("");
  const [debouncedChemistName, setDebouncedChemistName] = useState("");
  const [fetchError, setFetchError] = useState(null);
  const [timeChartOpen, setTimeChartOpen] = useState(false);

  useEffect(() => {
    const today = new Date();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(today.getDate() - 28);
    setDateStart(fourWeeksAgo.toISOString().split("T")[0]);
    setDateEnd(today.toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedChemistName(chemistFilter.trim()), 400);
    return () => clearTimeout(t);
  }, [chemistFilter]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const params = { DateStart: dateStart, DateEnd: dateEnd };
      if (debouncedChemistName) params.ChemistName = debouncedChemistName;
      const response = await phappsRequest(
        isManager ? API_PATHS.MANAGER_FUNCTIONS_QUDA : API_PATHS.ADMIN_FUNCTIONS_QUDA,
        "GET",
        null,
        params
      );

      const orders = response.OrdersPerDay || [];
      const processed = preprocessOrders(orders, dateStart, dateEnd);
      setChartData(processed);
      const summary = response.Summary || {};
      const stats = {
        totalOrders: Number(summary.totalOrdersAcrossChemists ?? 0),
        averageDailyOrders: Number(summary.averageOrdersPerDay ?? 0).toFixed(2),
      };
      setData({
        ...response,
        stats,
      });
      setFetchError(null);
    } catch (err) {
      console.error("Error fetching admin Quda data:", err);
      setFetchError(getAdminApiErrorMessage(err, "Failed to load Quda data"));
      setData(null);
      setChartData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dateStart && dateEnd) {
      fetchData();
    }
  }, [dateStart, dateEnd, debouncedChemistName]);

  const handleDateRangeChange = (newRange) => {
    if (newRange && newRange.length === 2) {
      setDateStart(newRange[0].toISOString().split("T")[0]);
      setDateEnd(newRange[1].toISOString().split("T")[0]);
    }
  };

  const orderTableColumns = [
    {
      field: "chemistName",
      headerName: "Chemist",
      flex: 1,
      minWidth: 200,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const uuid = params.row.ChemistUUID;
        return (
          <Typography
            variant="body2"
            onClick={() => uuid && navigate(`${chemistBasePath}/${encodeURIComponent(uuid)}`, { state: { tab: 1 } })}
            sx={{ cursor: uuid ? "pointer" : "default", fontWeight: 500, "&:hover": { textDecoration: uuid ? "underline" : "none" } }}
          >
            {getChemistName(params.value)}
          </Typography>
        );
      },
    },
    {
      field: "TotalOrders",
      headerName: "Total Orders",
      width: 150,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) =>
        Number(value || 0).toLocaleString(undefined, {
          maximumFractionDigits: 0,
        }),
    },
    {
      field: "DispensaryOrders",
      headerName: "Dispensary",
      width: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "OTCOrders",
      headerName: "OTC",
      width: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "AvgOrdersPerDay",
      headerName: "Avg Orders/Day",
      width: 170,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) => Number(value || 0).toFixed(2),
    },
    {
      field: "actions",
      headerName: "Open",
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const uuid = params.row.ChemistUUID;
        return (
          <Button
            size="small"
            variant="contained"
            onClick={() =>
              uuid &&
              navigate(
                `${chemistBasePath}/${encodeURIComponent(uuid)}`,
                { state: { tab: 1 } }
              )
            }
            disabled={!uuid}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              backgroundColor: QUDA_COLORS.TEAL_DARK,
              minWidth: 68,
              height: 32,
            }}
          >
            Open
          </Button>
        );
      },
    },
  ];

  const orderTableRows =
    data?.OverviewTable?.map((row, index) => ({
      id: row?.ChemistUUID || `${row?.chemistName || "row"}-${index}`,
      ...row,
    })) || [];

  const pieSlices = useMemo(() => {
    const dSum = orderTableRows.reduce(
      (acc, row) => acc + (parseInt(row.DispensaryOrders, 10) || 0),
      0
    );
    const oSum = orderTableRows.reduce(
      (acc, row) => acc + (parseInt(row.OTCOrders, 10) || 0),
      0
    );
    const slices = [
      {
        x: "Dispensary",
        y: dSum,
        text: "Dispensary",
        color: QUDA_COLORS.TEAL_DARK,
      },
      {
        x: "OTC",
        y: oSum,
        text: "OTC",
        color: QUDA_COLORS.PURPLE_DARK,
      },
    ];
    if (dSum > 0 || oSum > 0) return slices.filter((s) => s.y > 0);
    return slices;
  }, [orderTableRows]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: QUDA_COLORS.TEAL_DARK }}
        >
          Admin Quda overview
        </Typography>
        {fetchError ? (
          <Alert severity="warning">{fetchError}</Alert>
        ) : null}

        {isLoading && data == null ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading Quda data…</p>
            </div>
          </div>
        ) : data ? (
          <>
            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <Box sx={qudaDateRangeSx}>
                <SFDateRangeSelector
                  adminToolbar
                  value={[new Date(dateStart), new Date(dateEnd)]}
                  onChange={handleDateRangeChange}
                />
              </Box>

              <div className="min-w-0">
                <AdminQudaStatCard
                  title="Total Orders"
                  value={data?.stats?.totalOrders ?? 0}
                  Icon={ShoppingCart}
                  valueFormatter={(v) => Number(v).toLocaleString()}
                />
              </div>

              <div className="min-w-0">
                <AdminQudaStatCard
                  title="Avg Orders/Day"
                  value={data?.stats?.averageDailyOrders ?? 0}
                  Icon={TrendingUp}
                  valueFormatter={(v) => Number(v).toFixed(2)}
                />
              </div>

              <div className="min-w-0">
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  height={ADMIN_TOP_CARD_HEIGHT}
                  minHeight={ADMIN_TOP_CARD_HEIGHT}
                  maxHeight={ADMIN_TOP_CARD_HEIGHT}
                  boxSizing="border-box"
                  justifyContent="space-between"
                  sx={{ overflow: "hidden" }}
                >
                  <Box
                    flex={1}
                    bgcolor={QUDA_COLORS.PURPLE_DARK}
                    borderRadius="8px"
                    p={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ minHeight: 0, overflow: "hidden" }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "white", opacity: 0.8 }}
                        noWrap
                      >
                        Top Pharmacy
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="white"
                        noWrap
                      >
                        {getChemistName(data?.TopChemist?.chemistName) || "N/A"}
                      </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="white"
                        sx={{ lineHeight: 1 }}
                      >
                        {data?.TopChemist?.TotalOrders ?? "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="white"
                        sx={{ opacity: 0.8 }}
                      >
                        Orders
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    flex={1}
                    bgcolor={QUDA_COLORS.PURPLE_DARK}
                    borderRadius="8px"
                    p={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ minHeight: 0 }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "white", opacity: 0.8 }}
                        noWrap
                      >
                        Top Staff
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="white"
                        noWrap
                      >
                        {data?.TopStaffMember?.StaffName ||
                          data?.Summary?.topStaffByInteractions?.staffName ||
                          "N/A"}
                      </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="white"
                        sx={{ lineHeight: 1 }}
                      >
                        {data?.TopStaffMember?.interactionCount ??
                          data?.Summary?.topStaffByInteractions
                            ?.interactionCount ??
                          "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="white"
                        sx={{ opacity: 0.8 }}
                      >
                        Interactions
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </div>
            </section>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderColor: QUDA_COLORS.TEAL_DARK,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography fontWeight={700} sx={{ color: QUDA_COLORS.TEAL_DARK }}>
                      Chemists
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setTimeChartOpen(true)}
                        sx={{
                          textTransform: "none",
                          borderColor: QUDA_COLORS.TEAL_DARK,
                          color: QUDA_COLORS.TEAL_DARK,
                          fontWeight: 600,
                        }}
                      >
                        See orders over time
                      </Button>
                      <TextField
                        size="small"
                        placeholder="Filter by chemist name"
                        value={chemistFilter}
                        onChange={(e) => setChemistFilter(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 260 }}
                      />
                    </Box>
                  </Box>
                  <Divider />
                  <div
                    style={{ height: 520, width: "100%", padding: "0 8px 16px" }}
                  >
                    <DataGrid
                      rows={orderTableRows}
                      columns={orderTableColumns}
                      loading={isLoading}
                      pageSizeOptions={[10, 20, 50]}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 20 } },
                      }}
                      disableRowSelectionOnClick
                      sx={qudaAdminDataGridSx}
                    />
                  </div>
                </CardContent>
              </Card>

              <Box
                sx={{
                  width: { xs: "100%", lg: 380 },
                  flexShrink: 0,
                  minHeight: 400,
                  "& > div": { marginTop: "0 !important" },
                }}
              >
                <ChartCard
                  title="Order types"
                  hideMainChart
                  pieChartMinHeight={552}
                  data={{}}
                  primaryXAxis={{}}
                  primaryYAxis={{}}
                  borderColor={QUDA_COLORS.TEAL_DARK}
                  series={[]}
                  pieData={pieSlices}
                  pieTitle="Dispensary vs OTC"
                />
              </Box>
            </Box>

            <Dialog
              open={timeChartOpen}
              onClose={() => setTimeChartOpen(false)}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle sx={{ color: QUDA_COLORS.TEAL_DARK, fontWeight: 700 }}>
                Orders over time
              </DialogTitle>
              <DialogContent>
                <Box sx={{ "& > div": { mt: 0 }, pt: 1 }}>
                  <ChartCard
                    title=""
                    hidePieChart
                    mainChartMinHeight={360}
                    data={chartData}
                    primaryXAxis={{
                      valueType: "DateTime",
                      labelFormat: "dd/MM",
                      intervalType: "Days",
                      edgeLabelPlacement: "Shift",
                      majorGridLines: { width: 0 },
                    }}
                    primaryYAxis={{
                      labelFormat: "{value}",
                      title: "Orders",
                      minimum: 0,
                      majorGridLines: { width: 0 },
                    }}
                    borderColor={QUDA_COLORS.TEAL_DARK}
                    series={[
                      {
                        dataSource: chartData,
                        xName: "OrderDate",
                        yName: "DispensaryOrders",
                        name: "Dispensary Orders",
                        type: "StackingColumn",
                        fill: QUDA_COLORS.TEAL_DARK,
                        cornerRadius: { topLeft: 4, topRight: 4 },
                      },
                      {
                        dataSource: chartData,
                        xName: "OrderDate",
                        yName: "OTCOrders",
                        name: "OTC Orders",
                        type: "StackingColumn",
                        fill: QUDA_COLORS.PURPLE_DARK,
                        cornerRadius: { topLeft: 4, topRight: 4 },
                      },
                    ]}
                    pieData={[]}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setTimeChartOpen(false)} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        ) : !isLoading ? (
          <Typography color="textSecondary">No Quda data available.</Typography>
        ) : null}
      </div>
    </div>
  );
};

export default AdminQudaPage;
